import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { HiFingerPrint } from "react-icons/hi";
import { CustomButton } from "../../atoms/CustomButton";
import {
  isPasskeySupported,
  notifyPasskeyError,
  usePasskeyLogin,
} from "../../../hooks/auth/passwordless";

type Props = {
  email: string;
  entityIdentification?: string;
  onAuthenticated: (userData: any, userEmail: string) => void;
};

/**
 * Passkey login. Hidden entirely when the browser has no WebAuthn support, rather than shown
 * disabled: an option that can never work is noise on a login screen.
 */
export const PasskeyLoginButton = ({
  email,
  entityIdentification,
  onAuthenticated,
}: Props) => {
  const { t } = useTranslation();
  const { mutate: loginWithPasskey, isLoading } = usePasskeyLogin();

  if (!isPasskeySupported()) {
    return null;
  }

  const onClick = () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      toast.error(t("passkey.emailFirst"));
      return;
    }

    loginWithPasskey(
      {
        user_email: trimmedEmail,
        entity_identification: entityIdentification?.trim() || undefined,
      },
      {
        onSuccess: (response) => {
          const userData = response?.data?.element;
          if (!userData?.access_token) {
            toast.error(t("passkey.error"));
            return;
          }
          onAuthenticated(userData, trimmedEmail);
        },
        onError: (error: any) => {
          notifyPasskeyError(error, t, toast);
        },
      }
    );
  };

  return (
    <CustomButton
      variant="secondary"
      size="md"
      className="w-full"
      icon={<HiFingerPrint size={20} className="shrink-0 text-gray-500" />}
      title={t("passkey.loginBtn")}
      isLoading={isLoading}
      disabled={isLoading}
      onClick={onClick}
    />
  );
};

export default PasskeyLoginButton;
